import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, Loader2, RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

function RunStatus({ run }) {
  if (!run) return null;
  const done = run.results?.filter((r) => r.status === "ok").reduce((n, r) => n + r.suggested, 0) || 0;
  return (
    <p className="text-xs text-stone-600 mt-2" data-testid="city-quotes-last-run">
      Last collection: {run.status === "running" ? `running (${run.results?.length || 0}/${run.slugs.length} cities)` : `${done} new suggestions from ${run.slugs.length} cities`} · {run.started_at?.slice(0, 16).replace("T", " ")}
    </p>
  );
}

function QuoteRow({ q, decide, busy }) {
  return (
    <li className="rounded border border-stone-200 bg-white p-3" data-testid={`city-quote-${q.id}`}>
      <p className="text-sm text-stone-900">“{q.quote}”</p>
      <p className="text-xs text-stone-500 mt-1 flex flex-wrap items-center gap-x-2">
        <span className="font-medium text-stone-700">{q.city}</span>
        <span>{q.source}{q.context ? ` · ${q.context}` : ""}</span>
        <a href={q.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline" data-testid={`city-quote-link-${q.id}`}>thread <ExternalLink className="h-3 w-3" /></a>
      </p>
      <div className="flex gap-2 mt-2">
        <Button size="sm" disabled={busy} onClick={() => decide(q.id, true)} data-testid={`city-quote-approve-${q.id}`}><Check className="h-4 w-4" /> Approve</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => decide(q.id, false)} data-testid={`city-quote-reject-${q.id}`}><X className="h-4 w-4" /> Reject</Button>
      </div>
    </li>
  );
}

export function CityQuotesQueue() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/city-quotes?status=pending").then(({ data }) => setData(data)).catch(() => toast.error("Could not load quotes")), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.last_run?.status !== "running") return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);
  const collect = async () => {
    try { await api.post("/admin/city-quotes/collect", {}); toast.success("Collecting quotes for the featured cities"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };
  const decide = async (id, approve) => {
    setBusy(id);
    try {
      await api.post(`/admin/city-quotes/${id}/decision`, { approve });
      setData((d) => ({ ...d, items: d.items.filter((q) => q.id !== id) }));
    } catch { toast.error("Could not save"); } finally { setBusy(null); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-stone-400" />;
  const running = data.last_run?.status === "running";
  return (
    <section className="border border-stone-200 rounded-lg p-5" data-testid="city-quotes-queue">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">Student quotes waiting for review</h2>
          <p className="text-sm text-stone-600 mt-1 max-w-xl">Found in public Reddit threads (Google-indexed snippets); Claude suggests the lines, each kept word-for-word. Only approved quotes appear on the homepage.</p>
          <p className="text-xs text-stone-500 mt-1" data-testid="city-quotes-counts">{data.counts.pending || 0} pending · {data.counts.approved || 0} approved · {data.counts.rejected || 0} rejected</p>
          <RunStatus run={data.last_run} />
        </div>
        <Button variant="outline" onClick={collect} disabled={running} data-testid="city-quotes-collect">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Collect quotes for featured cities
        </Button>
      </div>
      <ul className="space-y-2 mt-4">
        {data.items.length ? data.items.map((q) => <QuoteRow key={q.id} q={q} decide={decide} busy={busy === q.id} />)
          : <li className="text-sm text-stone-500" data-testid="city-quotes-empty">Nothing waiting. Run a collection to find new quotes.</li>}
      </ul>
    </section>
  );
}
